import { CapabilityReason } from './capabilities';

/**
 * How a gated control should present, derived from the two questions that
 * decide everything (see docs/capability-gating-ui.md):
 *
 *     gate(capability)      -> Available | WithheldByLicence | WithheldByPlanAccess
 *     isInPlan(capability)  -> bool
 *
 * Pure and angular-free so it can be unit tested — the specs run under
 * jasmine-ts in Node with no DOM, and a module that reaches the app drags in
 * angular.js, which touches `window` at import time.
 */

/**
 * The three-valued gate. `CapabilityReason` carries a fourth state, `unknown`,
 * which is not a gate answer at all — it means no snapshot reached the page.
 * Kept separate below rather than folded in here.
 */
export type CapabilityGate = 'available' | 'withheldByLicence' | 'withheldByPlanAccess';

/**
 * What the author declared should happen when the capability is withheld —
 * #510's three presentations, one value each.
 *
 * - `hide` (the default) — the control is removed. No trace of the capability.
 * - `disable` — the control stays, visibly inert, carrying no upgrade message.
 *   This is the SECOND and subsequent entry point to a capability sold
 *   elsewhere: the gate must hold on every route, but only one route sells.
 * - `entice` — the control stays and stays LIVE, looking exactly as it does for
 *   an entitled user. The pitch is not on the control; it is one step beyond
 *   it, on whatever the control opens.
 *
 * `entice` deliberately changes nothing about the element. A menu item that
 * advertises an upgrade in its own label is the design this replaced: the user
 * is told "no" before they have said what they want. Instead the click is
 * allowed, and the destination — an inline panel, or the generic upsell dialog
 * where there is no natural destination — does the selling, somewhere with room
 * to explain. See `gpCapabilityUpsell`.
 */
export type WithheldIntent = 'hide' | 'disable' | 'entice';

const WITHHELD_INTENTS: readonly string[] = ['hide', 'disable', 'entice'];

/**
 * Reads the `gp-withheld` attribute value. Anything unrecognised falls back to
 * `hide`, matching the attribute being absent — a misspelt intent must never
 * become permission to keep a withheld control alive.
 */
export function parseWithheldIntent(value: string | undefined | null): WithheldIntent {
    const trimmed = (value ?? '').trim();
    return WITHHELD_INTENTS.indexOf(trimmed) === -1 ? 'hide' : trimmed as WithheldIntent;
}

/**
 * What the element resolves to. `residue` is the "state the user did not
 * create still renders" rule (#566): withheld, but the plan already contains
 * this feature's content, so the existing output stays visible.
 */
export type CapabilityPresentation =
    'normal' | 'hidden' | 'disabled' | 'enticed' | 'residue' | 'unknown';

export interface PresentationInputs {
    reason: CapabilityReason;
    intent: WithheldIntent;
    keepIfInPlan: boolean;
    isInPlan: boolean;
}

/** Maps a verdict reason onto the three-valued gate, or null for `unknown`. */
export function gateFromReason(reason: CapabilityReason): CapabilityGate | null {
    switch (reason) {
        case 'enabled':
            return 'available';
        case 'notEntitled':
            return 'withheldByLicence';
        case 'insufficientPlanAccess':
            return 'withheldByPlanAccess';
        case 'unknown':
            return null;
    }
}

/**
 * The whole decision, in one place.
 *
 * Three rules here are easy to get wrong, and are why this is a function rather
 * than a couple of ternaries at the call site:
 *
 * 1. `withheldByPlanAccess` IGNORES both other intents and never keeps residue.
 *    Upgrading does not fix a plan-access shortfall, so a substitute offering
 *    an upgrade there would be a lie and a greyed control an unexplained dead
 *    end. The author never writes this — it falls out of the verdict.
 *
 * 2. `keepIfInPlan` only applies to a licence withholding. It is about what the
 *    user's own licence will not let them CREATE, not about someone else's plan
 *    they can only read.
 *
 * 3. `unknown` presents as `unknown`, which the directive leaves alone. No
 *    snapshot reached the page, which is a broken page rather than a small
 *    licence; hiding the product on the strength of an answer nobody gave is
 *    the one failure worse than not gating at all.
 */
export function resolvePresentation(inputs: PresentationInputs): CapabilityPresentation {
    const gate = gateFromReason(inputs.reason);

    if (gate === null) {
        return 'unknown';
    }

    if (gate === 'available') {
        return 'normal';
    }

    if (gate === 'withheldByPlanAccess') {
        return 'hidden';
    }

    if (inputs.keepIfInPlan && inputs.isInPlan) {
        return 'residue';
    }

    switch (inputs.intent) {
        case 'disable':
            return 'disabled';
        case 'entice':
            return 'enticed';
        case 'hide':
            return 'hidden';
    }
}

/**
 * Should a `gp-if-withheld` substitute render?
 *
 * Only for a licence withholding — never for a plan-access one, so upgrade copy
 * never reaches a user who cannot act on it, and never when the capability is
 * available, or the substitute would sit beside the real control.
 *
 * It DOES render alongside `residue`: a plan holding costs the user cannot edit
 * is exactly the case where an upgrade panel above the read-only totals earns
 * its space — they can see what they are missing.
 */
export function shouldRenderSubstitute(reason: CapabilityReason): boolean {
    return gateFromReason(reason) === 'withheldByLicence';
}

/**
 * May the action behind a gated control actually run?
 *
 * The gate decides what RENDERS; this decides what HAPPENS when the control is
 * reached anyway — through a keyboard, a right-click, or an `entice` that left
 * it deliberately live. Both must reach the same verdict from the same reason,
 * which is why the rule lives here rather than at each call site.
 *
 * `unknown` permits. No snapshot reached the page, which is a broken page
 * rather than a small licence, and refusing an action on the strength of an
 * answer nobody gave is the one failure worse than not gating at all.
 *
 * It says nothing about what to do with a refusal — that is the caller's
 * business, and the right answer differs between the two ways of being
 * withheld.
 */
export function permitsAction(reason: CapabilityReason): boolean {
    const gate = gateFromReason(reason);
    return gate === null || gate === 'available';
}

/**
 * The classes that do the gating, styled in `capability-gating.less` and
 * nowhere else.
 *
 * Only two of the six presentations earn one. `normal`, `enticed`, `residue`
 * and `unknown` all mean "render this exactly as authored" — an enticed control
 * is deliberately indistinguishable from an available one, and a kept section
 * is made read-only by the controls INSIDE it carrying their own
 * `gp-capability`, not by a wrapper neutralising arbitrary descendants.
 */
export const CAPABILITY_GATING_CLASSES = [
    'gp-capability-hidden',
    'gp-capability-disabled',
] as const;

export type CapabilityGatingClass = typeof CAPABILITY_GATING_CLASSES[number];

/** The gating class a presentation earns, or null when it is left alone. */
export function gatingClassFor(presentation: CapabilityPresentation): CapabilityGatingClass | null {
    switch (presentation) {
        case 'hidden':
            return 'gp-capability-hidden';
        case 'disabled':
            return 'gp-capability-disabled';
        case 'normal':
        case 'enticed':
        case 'residue':
        case 'unknown':
            return null;
    }
}
